import CommonTable, {
  IActionData,
  ITableColumn,
} from '@/components/common/CommonTable';
import MainHeading from '@/components/common/MainHeading';
import PlusIcon from '@/components/Icons/PlusIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import { RootState } from '@/redux/store';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AddAssetRequestModal from './AddAssetRequestModal';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import EditIcon from '@/components/Icons/EditIcon';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import {
  showToastNotification,
  getFilterUrl,
  orderByList,
} from '@/utility/helper';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import ActionModal from '@/components/Modal/ActionModal';
import TransferModal from '@/components/Modal/TransferModal';
import AssetTransferIcon from '@/components/Icons/AssetTransferIcon';
import BackArrowIcon from '@/components/Icons/BackArrowIcon';
import {
  IAssetRequestApiResponse,
  IAssetRequestUpdate,
  IAssetTableData,
} from '@/interfaces/assets.model';
import { IDeleteResponse } from '@/interfaces/common.model';
import ReturnAssetIcon from '@/components/Icons/ReturnAssetIcon';
import useFilter from '@/hooks/useFilter';
import FilterComponent from '@/components/common/FilterComponent';
import ViewIcon from '@/components/Icons/ViewIcon';
import AssetRequestDetailModal from '@/components/Modal/AssetRequestDetailModal';
import moment from 'moment';

interface IPageStates {
  assetRequestList: IAssetTableData[];
  totalPage: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  isUpdating: boolean;
  totalPages: number;
  count: number;
  editData: IAssetTableData | null;
  deleteId: string | null;
  isDeleteModalOpen: boolean;
  isTransferModalOpen: boolean;
  isReturnModalOpen: boolean;
  requestId: string | null;
}
const AssetsRequest = () => {
  const [modal, setModal] = useState<boolean>(false);
  const userDetails = useSelector((state: RootState) => state?.user?.user);
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<IAssetTableData | null>(null);
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const [pageStates, setPageStates] = useState<IPageStates>({
    assetRequestList: [],
    totalPage: 0,
    currentPage: 1,
    pageSize: filterDetails?.pageSize,
    isUpdating: false,
    isLoading: false,
    totalPages: 0,
    count: 0,
    editData: null,
    deleteId: null,
    isDeleteModalOpen: false,
    isTransferModalOpen: false,
    isReturnModalOpen: false,
    requestId: null,
  });
  const tableHeading: ITableColumn<IAssetTableData>[] = [
    { value: 'index', label: '#' },
    { value: 'asset', label: 'Asset' },
    { value: 'details', label: 'Details' },
    // { value: 'assetCategory', label: 'Asset Category' },
    // { value: "description", label: "Description" },
    // { value: "receivedOn", label: "Received On" },
    { value: 'assetRequestStatus', label: 'Status' },
  ];
  const actionData: IActionData<IAssetTableData>[] = [
    {
      label: 'View',
      className: 'btn btn-link',
      icon: <ViewIcon />,
      title: 'view',
      clickHandler: (data: IAssetTableData) => {
        setSelectedRequest(data);
        setIsDetailModalOpen(true);
      },
    },
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'Edit',
      clickHandler: (data: IAssetTableData) => {
        setPageStates((prev) => ({ ...prev, editData: data }));
        setModal(true);
      },
      disabled: (data: IAssetTableData) => Number(data.assetRequestStatus) == 2,
      isHidden: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) == 3 ||
        Number(data.assetRequestStatus) == 1,
    },
    {
      label: 'Delete',
      className: 'btn btn-link',
      icon: <DeleteIcon />,
      title: 'Delete',
      clickHandler: (data: IAssetTableData) => {
        setPageStates((prev) => ({
          ...prev,
          deleteId: data.id,
          isDeleteModalOpen: true,
        }));
      },
      disabled: (data: IAssetTableData) => Number(data.assetRequestStatus) == 2,
      isHidden: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) == 3 ||
        Number(data.assetRequestStatus) == 1,
    },
    {
      label: 'Transfer',
      className: 'btn btn-link',
      icon: <AssetTransferIcon />,
      title: 'Transfer',
      clickHandler: (data: IAssetTableData) => {
        setPageStates((prev) => ({
          ...prev,
          requestId: data.id,
          isTransferModalOpen: true,
        }));
      },
      disabled: (data: IAssetTableData) => Number(data.assetRequestStatus) == 3,
      isHidden: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) == 0 ||
        Number(data.assetRequestStatus) == 2,
    },
    {
      label: 'Return',
      className: 'btn btn-link',
      icon: <ReturnAssetIcon />,
      title: 'Return',
      clickHandler: (data: IAssetTableData) => {
        setPageStates((prev) => ({
          ...prev,
          requestId: data.id,
          isReturnModalOpen: true,
        }));
      },
      disabled: (data: IAssetTableData) => Number(data.assetRequestStatus) == 3,
      isHidden: (data: IAssetTableData) =>
        Number(data.assetRequestStatus) == 0 ||
        Number(data.assetRequestStatus) == 2,
    },
  ];
  useEffect(() => {
    getAssetRequestList();
  }, [pageStates.currentPage, filterDetails.pageSize, filterDetails?.orderBy]);

  const toggle = () => {
    setModal(!modal);
  };
  const getAssetRequestList = async () => {
    try {
      setPageStates((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const filter = {
        assignedId: userDetails?.id,
        pageSize: filterDetails?.pageSize,
        pageNumber: pageStates.currentPage,
        orderBy: filterDetails?.orderBy,
      };
      const response: IAssetRequestApiResponse = await callApi(
        API_URL + API_ENDPOINT.assetRequestList + '?' + getFilterUrl(filter),
        { method: 'GET' },
      );
      if (response?.status) {
        console.log(response?.data?.['asset Request']);
        const newData: IAssetTableData[] = response?.data?.[
          'asset Request'
        ]?.map((item, index) => ({
          ...item,
          id: item.id,
          index: index + 1,
          requestedBy: item?.user?.name ?? '-',
          requestedOn: moment(item.createdAt).format('DD-MM-YYYY') ?? '-',
          asset: item.asset?.name ?? '(Not Assigned)',
          assetCategory: item.assetCategory?.name ?? '-',
          assetCategoryId: item.assetCategory?.id ?? '',
          description: item.description ?? '-',
          requirements: item.description ?? '-',
          receivedAt: item.receivedAt ?? '-',
          assetRequestStatus: Number(item.status),
        }));
        setPageStates((pre) => ({
          ...pre,
          assetRequestList: newData,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages ?? 0,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleDeleteRequest = async () => {
    if (!pageStates.deleteId) return;
    try {
      setPageStates((prev) => ({ ...prev, isUpdating: true }));
      const response = await callApi(
        API_URL +
          API_ENDPOINT.deleteAssetRequest +
          '?assetRequestId=' +
          pageStates.deleteId,
        { method: 'DELETE' },
      );
      if (response) {
        showToastNotification('success', 'Asset request deleted successfully');
        getAssetRequestList();
        setPageStates((prev) => ({
          ...prev,
          isDeleteModalOpen: false,
          deleteId: null,
        }));
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Failed to delete request');
      }
    } finally {
      setPageStates((prev) => ({ ...prev, isUpdating: false }));
    }
  };

  const handleTransferReturn = async (
    type: 'transfer' | 'return',
    assignedId?: string,
  ) => {
    if (!pageStates.requestId) return;
    const data: IAssetRequestUpdate = {
      ...(type === 'transfer'
        ? { isTransfer: true, assignedId }
        : { isReturn: true }),
    };
    try {
      setPageStates((prev) => ({ ...prev, isUpdating: true }));
      const response: IDeleteResponse = await callApi(
        `${API_URL + API_ENDPOINT.updateAssetRequest}?assetRequestId=${pageStates.requestId}`,
        { method: 'POST', data: data },
      );
      if (response?.status) {
        showToastNotification(
          'success',
          `Asset ${type === 'transfer' ? 'Transfer' : 'Return'} successfully`,
        );
        getAssetRequestList();
        setPageStates((prev) => ({
          ...prev,
          isTransferModalOpen: false,
          isReturnModalOpen: false,
          requestId: null,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({ ...prev, isUpdating: false }));
    }
  };
  const { orderByHandler, handleChangePageSize, pageChangeHandler } = useFilter(
    pageStates,
    setPageStates,
  );
  return (
    <>
      <Layout>
        <div className="content">
          <MainHeading
            title={'Asset Requests'}
            buttonText={'Create Request'}
            buttonIcon={<PlusIcon />}
            clickHandler={toggle}
          />
        </div>

        <CardLayout cardStyle="mb-4" isGrid={tableView === 'grid'}>
          <FilterComponent
            orderByList={orderByList}
            orderByHandler={orderByHandler}
          />
          <CommonTable<IAssetTableData>
            tableHeading={tableHeading}
            tableData={pageStates.assetRequestList}
            ActionData={actionData}
            count={pageStates?.count}
            pageSize={filterDetails?.pageSize}
            isLoading={pageStates?.isLoading}
            currentPage={pageStates?.currentPage}
            totalPages={pageStates?.totalPages}
            pageChangeHandler={pageChangeHandler}
            handleChangePageSize={handleChangePageSize}
          />
        </CardLayout>
      </Layout>
      <AddAssetRequestModal
        isOpen={modal}
        onClose={() => {
          toggle();
          setPageStates((prev) => ({ ...prev, editData: null }));
        }}
        editData={pageStates.editData}
        getRequests={getAssetRequestList}
      />
      <ConfirmModal
        isOpen={pageStates.isDeleteModalOpen}
        toggle={() =>
          setPageStates((prev) => ({
            ...prev,
            isDeleteModalOpen: !prev.isDeleteModalOpen,
            deleteId: null,
          }))
        }
        submitHandler={handleDeleteRequest}
        isLoading={pageStates.isUpdating}
        hideReason={true}
      />
      <TransferModal
        isOpen={pageStates.isTransferModalOpen}
        isLoading={pageStates.isUpdating}
        submitHandler={(assignedId) =>
          handleTransferReturn('transfer', assignedId)
        }
        toggle={() =>
          setPageStates((prev) => ({
            ...prev,
            isTransferModalOpen: !prev.isTransferModalOpen,
            requestId: null,
          }))
        }
      />
      <ActionModal
        isOpen={pageStates.isReturnModalOpen}
        isLoading={pageStates.isUpdating}
        submitHandler={() => handleTransferReturn('return')}
        toggle={() =>
          setPageStates((prev) => ({
            ...prev,
            isReturnModalOpen: !prev.isReturnModalOpen,
            requestId: null,
          }))
        }
        title="Asset Return"
        message="Do you really want to return this asset?"
        icon={<BackArrowIcon />}
        btnText="Return"
        btnClass="btn-warning"
        iconClass="icon"
      />
      <AssetRequestDetailModal
        isOpen={isDetailModalOpen}
        toggle={() => setIsDetailModalOpen(false)}
        data={selectedRequest}
      />
    </>
  );
};
export default AssetsRequest;
